import { useState, useCallback, memo } from 'react';
import { cn } from '../../utils';

interface DocImageProps {
  src: string;
  alt: string;
  caption?: string;
  className?: string;
}

export const DocImage = memo(function DocImage({ src, alt, caption, className }: DocImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoaded(true);
  }, []);

  return (
    <figure className={cn('my-6', className)}>
      <div className="relative overflow-hidden rounded-lg border border-border bg-muted/30">
        {/* Shimmer placeholder */}
        {!isLoaded && (
          <div className="absolute inset-0 bg-muted animate-pulse">
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-background/50 to-transparent"
              style={{
                animation: 'shimmer 1.5s infinite',
                backgroundSize: '200% 100%',
              }}
            />
          </div>
        )}

        {hasError ? (
          <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
            Failed to load image
          </div>
        ) : (
          <img
            src={src}
            alt={alt}
            loading="lazy"
            onLoad={handleLoad}
            onError={handleError}
            className={cn(
              'w-full h-auto transition-opacity duration-300',
              isLoaded ? 'opacity-100' : 'opacity-0'
            )}
          />
        )}
      </div>

      {caption && (
        <figcaption className="mt-2 text-center text-sm text-muted-foreground">
          {caption}
        </figcaption>
      )}
    </figure>
  );
});
